import React from 'react';
import './ErrorHandling.css';

interface ErrorInfo {
  message?: string;
  code?: string | number;
  status?: string | number;
}

interface ErrorMessageProps {
  error: string | Error | ErrorInfo | null;
  title?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorToastProps {
  error: string | Error | ErrorInfo | null;
  isVisible: boolean;
  onDismiss: () => void;
  duration?: number;
  className?: string;
}

interface ErrorAlertProps {
  error: string | Error | ErrorInfo | null;
  variant?: 'error' | 'warning' | 'info' | 'success';
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

interface ErrorFallbackProps {
  error: Error | null;
  resetError: () => void;
  className?: string;
}

// Error Message Component
export function ErrorMessage({ 
  error, 
  title = 'Bir Hata Oluştu', 
  onRetry, 
  onDismiss,
  className = '' 
}: ErrorMessageProps) {
  if (!error) return null;

  const errorMessage = typeof error === 'string' ? error : (error as Error).message || (error as ErrorInfo).message || 'Bilinmeyen hata';
  const errorCode = (error as ErrorInfo).code || (error as ErrorInfo).status || null;

  return (
    <div className={`error-message ${className}`}>
      <div className="error-icon">❌</div>
      <div className="error-content">
        <h3 className="error-title">{title}</h3>
        <p className="error-text">{errorMessage}</p>
        {errorCode && (
          <p className="error-code">Hata Kodu: {errorCode}</p>
        )}
        <div className="error-actions">
          {onRetry && (
            <button className="error-btn error-btn-retry" onClick={onRetry}>
              🔄 Tekrar Dene
            </button>
          )}
          {onDismiss && (
            <button className="error-btn error-btn-dismiss" onClick={onDismiss}>
              ✕ Kapat
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Error Boundary Component
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error, errorInfo: null };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log error to console in development
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  override render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">⚠️</div>
            <h2>Bir Hata Oluştu</h2>
            <p>Uygulamada beklenmeyen bir hata meydana geldi.</p>
            <div className="error-boundary-actions">
              <button className="error-btn error-btn-retry" onClick={this.handleRetry}>
                🔄 Sayfayı Yenile
              </button>
              <button 
                className="error-btn error-btn-home" 
                onClick={() => window.location.href = '/'}
              >
                🏠 Ana Sayfaya Dön
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Hata Detayları</summary>
                <pre className="error-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Error Toast Component
export function ErrorToast({ 
  error, 
  isVisible, 
  onDismiss, 
  duration = 5000,
  className = '' 
}: ErrorToastProps) {
  React.useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        onDismiss();
      }, duration);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isVisible, duration, onDismiss]);

  if (!isVisible || !error) return null;

  const errorMessage = typeof error === 'string' ? error : (error as Error).message || (error as ErrorInfo).message || 'Bir hata oluştu';

  return (
    <div className={`error-toast ${className}`}>
      <div className="error-toast-content">
        <div className="error-toast-icon">❌</div>
        <div className="error-toast-text">
          <strong>Hata:</strong> {errorMessage}
        </div>
        <button className="error-toast-close" onClick={onDismiss}>
          ✕
        </button>
      </div>
    </div>
  );
}

// Error Alert Component
export function ErrorAlert({ 
  error, 
  variant = 'error', 
  dismissible = true,
  onDismiss,
  className = '' 
}: ErrorAlertProps) {
  if (!error) return null;

  const errorMessage = typeof error === 'string' ? error : (error as Error).message || (error as ErrorInfo).message || 'Bir hata oluştu';

  const getIcon = (): string => {
    switch (variant) {
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      case 'success':
        return '✅';
      default:
        return '❌';
    }
  };

  return (
    <div className={`error-alert error-alert-${variant} ${className}`}>
      <div className="error-alert-icon">{getIcon()}</div>
      <div className="error-alert-content">
        <p className="error-alert-text">{errorMessage}</p>
      </div>
      {dismissible && onDismiss && (
        <button className="error-alert-close" onClick={onDismiss}>
          ✕
        </button>
      )}
    </div>
  );
}

// Error Fallback Component
export function ErrorFallback({ 
  error, 
  resetError, 
  className = '' 
}: ErrorFallbackProps) {
  return (
    <div className={`error-fallback ${className}`}>
      <div className="error-fallback-content">
        <div className="error-fallback-icon">🔧</div>
        <h2>Bir Sorun Oluştu</h2>
        <p>Bu bölümde bir hata meydana geldi. Lütfen tekrar deneyin.</p>
        <div className="error-fallback-actions">
          <button className="error-btn error-btn-retry" onClick={resetError}>
            🔄 Tekrar Dene
          </button>
        </div>
        {process.env.NODE_ENV === 'development' && error && (
          <details className="error-details">
            <summary>Hata Detayları</summary>
            <pre className="error-stack">{error.toString()}</pre>
          </details>
        )}
      </div>
    </div>
  );
}

// Error Handler Hook
export function useErrorHandler() {
  const [error, setError] = React.useState<string | Error | ErrorInfo | null>(null);
  const [isVisible, setIsVisible] = React.useState<boolean>(false);

  const handleError = React.useCallback((error: string | Error | ErrorInfo) => {
    setError(error);
    setIsVisible(true);
  }, []);

  const clearError = React.useCallback(() => {
    setError(null);
    setIsVisible(false);
  }, []);

  const showError = React.useCallback((error: string | Error | ErrorInfo) => {
    handleError(error);
  }, [handleError]);

  const hideError = React.useCallback(() => {
    setIsVisible(false);
  }, []);

  return {
    error,
    isVisible,
    handleError,
    clearError,
    showError,
    hideError
  };
}

// Error Context
const ErrorContext = React.createContext<ReturnType<typeof useErrorHandler> | null>(null);

interface ErrorProviderProps {
  children: React.ReactNode;
}

export function ErrorProvider({ children }: ErrorProviderProps) {
  const errorHandler = useErrorHandler();

  return (
    <ErrorContext.Provider value={errorHandler}>
      {children}
    </ErrorContext.Provider>
  );
}

export function useError() {
  const context = React.useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
}

// Error Types
export const ERROR_TYPES = {
  NETWORK: 'network',
  VALIDATION: 'validation',
  PERMISSION: 'permission',
  NOT_FOUND: 'not_found',
  SERVER: 'server',
  CLIENT: 'client',
  UNKNOWN: 'unknown'
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'İnternet bağlantınızı kontrol edin',
  VALIDATION_ERROR: 'Lütfen gerekli alanları doldurun',
  PERMISSION_ERROR: 'Bu işlem için yetkiniz bulunmuyor',
  NOT_FOUND_ERROR: 'Aradığınız içerik bulunamadı',
  SERVER_ERROR: 'Sunucu hatası oluştu',
  CLIENT_ERROR: 'Bir hata oluştu',
  UNKNOWN_ERROR: 'Bilinmeyen bir hata oluştu'
} as const;

export default {
  ErrorMessage,
  ErrorBoundary,
  ErrorToast,
  ErrorAlert,
  ErrorFallback,
  useErrorHandler,
  ErrorProvider,
  useError,
  ERROR_TYPES,
  ERROR_MESSAGES
};

